import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../contexts/ThemeContext';
import { ArrowUpIcon } from '@heroicons/react/24/outline';

export default function TermsOfServicePage() {
  const { theme } = useTheme();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const textColor = theme === 'dark' ? 'text-gray-300' : 'text-gray-700';
  const headingColor = theme === 'dark' ? 'text-white' : 'text-gray-900';
  const bgColor = theme === 'dark' ? 'bg-gray-800' : 'bg-white';
  const [showScrollButton, setShowScrollButton] = useState(false);

  const handleGoBack = (e: React.MouseEvent) => {
    e.preventDefault();
    navigate(-1);
  };

  // Reset scroll position when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Show/hide scroll button based on scroll position
  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 300) {
        setShowScrollButton(true);
      } else {
        setShowScrollButton(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div className={`${bgColor} min-h-screen overflow-y-auto`}>
      <header className={`${theme === 'dark' ? 'bg-gray-900' : 'bg-white border-b border-gray-200'} py-4 sticky top-0 z-10`}>
        <div className="container mx-auto px-4 flex justify-between items-center">
          <Link to="/" className="text-2xl font-bold text-blue-600">{t('common.appName')}</Link>
          <a href="#" onClick={handleGoBack} className="text-sm font-medium text-blue-600 hover:underline">{t('navigation.goBack')}</a>
        </div>
      </header>
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-4xl mx-auto">
          {showScrollButton && (
            <button
              onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
              className={`fixed bottom-8 right-8 p-3 rounded-full shadow-lg bg-blue-600 text-white hover:bg-blue-700 transition-colors ${theme === 'dark' ? 'bg-blue-700 hover:bg-blue-800' : ''}`}
              aria-label="Scroll to top"
            >
              <ArrowUpIcon className="w-5 h-5" />
            </button>
          )}
          <h1 className={`text-3xl font-bold mb-6 ${headingColor}`}>{t('pages.termsOfService.title')}</h1>
          <p className={`mb-4 ${textColor}`}>{t('pages.termsOfService.effectiveDate')}</p>

          <section className="mb-8">
            <h2 className={`text-2xl font-semibold mb-3 ${headingColor}`}>{t('pages.termsOfService.acceptance.title')}</h2>
            <p className={`${textColor}`}>
              By using FlowCXP, you agree to these Terms. For organizations, you warrant authority to bind them.
            </p>
          </section>

          <section className="mb-8">
            <h2 className={`text-2xl font-semibold mb-3 ${headingColor}`}>{t('pages.termsOfService.serviceUse.title')}</h2>
            <p className={`mb-3 ${textColor}`}>You agree to:</p>
            <ul className={`list-disc pl-6 space-y-2 ${textColor}`}>
              <li>Comply with laws</li>
              <li>Provide accurate info</li>
              <li>Use only authorized access</li>
              <li>Not reverse engineer, hack, resell, etc.</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className={`text-2xl font-semibold mb-3 ${headingColor}`}>{t('pages.termsOfService.accountsBilling.title')}</h2>
            <ul className={`list-disc pl-6 space-y-2 ${textColor}`}>
              <li>Paid via subscription (monthly/annual)</li>
              <li>Payment must be valid; no refunds except as legally required</li>
              <li>Billing terms are detailed in invoices or plan descriptions</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className={`text-2xl font-semibold mb-3 ${headingColor}`}>{t('pages.termsOfService.userContent.title')}</h2>
            <ul className={`list-disc pl-6 space-y-2 ${textColor}`}>
              <li>You retain all IP rights in content</li>
              <li>You grant FlowCXP a license to host, reproduce, and display your content to provide Services</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className={`text-2xl font-semibold mb-3 ${headingColor}`}>{t('pages.termsOfService.privacy.title')}</h2>
            <p className={`${textColor}`}>
              Your data is processed per our Privacy Policy. You are the Controller; we are Processor for customer content.
            </p>
          </section>

          <section className="mb-8">
            <h2 className={`text-2xl font-semibold mb-3 ${headingColor}`}>{t('pages.termsOfService.confidentiality.title')}</h2>
            <p className={`${textColor}`}>
              Each party keeps confidential information private, excludes public knowledge or required disclosure. 
              Standard NDA-style terms.
            </p>
          </section>

          <section className="mb-8">
            <h2 className={`text-2xl font-semibold mb-3 ${headingColor}`}>{t('pages.termsOfService.security.title')}</h2>
            <p className={`${textColor}`}>
              We may engage subprocessors (e.g. AWS, Stripe). We remain responsible for their compliance.
            </p>
          </section>

          <section className="mb-8">
            <h2 className={`text-2xl font-semibold mb-3 ${headingColor}`}>{t('pages.termsOfService.suspension.title')}</h2>
            <ul className={`list-disc pl-6 space-y-2 ${textColor}`}>
              <li>We may suspend or terminate if you breach Terms or for legal reasons</li>
              <li>You can cancel anytime; data retention/deletion described in Privacy Policy</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className={`text-2xl font-semibold mb-3 ${headingColor}`}>{t('pages.termsOfService.disclaimers.title')}</h2>
            <ul className={`list-disc pl-6 space-y-2 ${textColor}`}>
              <li>Services provided "as-is" without warranties</li>
              <li>Liability limited to direct damages up to total fees paid in past 12 months; excludes indirect/consequential damages</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className={`text-2xl font-semibold mb-3 ${headingColor}`}>{t('pages.termsOfService.indemnification.title')}</h2>
            <p className={`${textColor}`}>
              You agree to indemnify FlowCXP from claims arising from your use of the Services or your content.
            </p>
          </section>

          <section className="mb-8">
            <h2 className={`text-2xl font-semibold mb-3 ${headingColor}`}>{t('pages.termsOfService.governingLaw.title')}</h2>
            <ul className={`list-disc pl-6 space-y-2 ${textColor}`}>
              <li>Governed by Singapore law</li>
              <li>Disputes resolved in courts of Singapore</li>
              <li>If you are outside Singapore, replace with your region</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className={`text-2xl font-semibold mb-3 ${headingColor}`}>{t('pages.termsOfService.modifications.title')}</h2>
            <p className={`${textColor}`}>
              We may change these Terms; continued use after change implies acceptance.
            </p>
          </section>

          <section className="mb-8">
            <h2 className={`text-2xl font-semibold mb-3 ${headingColor}`}>{t('pages.termsOfService.entireAgreement.title')}</h2>
            <p className={`${textColor}`}>
              Includes Terms, Privacy Policy, and any service-related addenda; supersedes prior agreements.
            </p>
          </section>

          <section className="mb-8">
            <h2 className={`text-2xl font-semibold mb-3 ${headingColor}`}>{t('pages.termsOfService.contact.title')}</h2>
            <p className={`${textColor}`}>
              For questions, email <a href="mailto:admin@flowcxp.com" className="text-blue-500 hover:underline">admin@flowcxp.com</a>.
            </p>
          </section>
        </div>
      </div>
      <footer className={`${theme === 'dark' ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} border-t py-4`}>
        <div className="container mx-auto px-4">
          <div className={`flex items-center justify-between text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
            <div>&copy; {new Date().getFullYear()} {t('common.appName')}. {t('common.allRightsReserved')}</div>
            <div className="flex space-x-4">
              <Link to="/privacy-policy" className={`${theme === 'dark' ? 'hover:text-gray-200' : 'hover:text-gray-900'}`}>{t('navigation.privacyPolicy')}</Link>
              <Link to="/terms-of-service" className={`${theme === 'dark' ? 'hover:text-gray-200' : 'hover:text-gray-900'}`}>{t('navigation.termsOfService')}</Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
